import React, { useState, useMemo, useEffect } from 'react';
import {
  BrioAnalyticsSurface, BrioFrame, BrioByCode, BrioTable,
  BrioDatePicker, BrioSelect,
  getTablePropsFromCode, useFullScreenPage, BrioFullScreenButton
} from '../../components/BrioComponents';
import { useSettings } from '../../hooks/useSettings';
import {
  buildUnifiedExpenseList, filterUnifiedExpenses, computeExpenseTotals,
  SOURCE_TYPE_LABELS, SOURCE_TYPE_OPTIONS,
} from '../../utils/expenseDataAdapters';

const PRESENTATION_CODE = '1b';

export default function ExpenseAnalyticsPage({
  activeProjects, activeUsers, activeExpenses, activeOfficeOverheads,
  fmtProject, reimbursementsHook, invoicesHook, onNavigate,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const { isFullScreen, toggle: toggleFullScreen, rootStyle, rootClassName } = useFullScreenPage(true);
  const settings = useSettings();
  const displayCurrency = settings.displayCurrency || 'INR';
  const convertMoney = settings.convertMoney;
  const formatMoney = settings.formatMoney || ((v) => String(v));
  const displaySymbol = displayCurrency === 'EUR' ? '€' : '₹';

  const { reimbursements } = reimbursementsHook || {};
  const paymentInboxExpenses = invoicesHook?.expensePayments || [];

  // Ensure payment inbox expenses are fetched when this page mounts
  useEffect(() => {
    if (invoicesHook?.fetchExpensePayments) {
      invoicesHook.fetchExpensePayments();
    }
  }, [invoicesHook?.fetchExpensePayments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filters — restored from localStorage
  const [filterYear,       setFilterYear]       = useState(() => { try { return JSON.parse(localStorage.getItem('brio_analytics_expenses_filters') || '{}').year       || ''; } catch { return ''; } });
  const [filterDateFrom,   setFilterDateFrom]   = useState(() => { try { return JSON.parse(localStorage.getItem('brio_analytics_expenses_filters') || '{}').dateFrom   || ''; } catch { return ''; } });
  const [filterDateTo,     setFilterDateTo]     = useState(() => { try { return JSON.parse(localStorage.getItem('brio_analytics_expenses_filters') || '{}').dateTo     || ''; } catch { return ''; } });
  const [filterSourceType, setFilterSourceType] = useState(() => { try { return JSON.parse(localStorage.getItem('brio_analytics_expenses_filters') || '{}').sourceType || ''; } catch { return ''; } });
  const [filterProject,    setFilterProject]    = useState(() => { try { return JSON.parse(localStorage.getItem('brio_analytics_expenses_filters') || '{}').project    || ''; } catch { return ''; } });
  const [filterOffice,     setFilterOffice]     = useState(() => { try { return JSON.parse(localStorage.getItem('brio_analytics_expenses_filters') || '{}').office     || ''; } catch { return ''; } });
  const [filterBillable,   setFilterBillable]   = useState(() => { try { return JSON.parse(localStorage.getItem('brio_analytics_expenses_filters') || '{}').billable   || ''; } catch { return ''; } });

  useEffect(() => {
    try { localStorage.setItem('brio_analytics_expenses_filters', JSON.stringify({ year: filterYear, dateFrom: filterDateFrom, dateTo: filterDateTo, sourceType: filterSourceType, project: filterProject, office: filterOffice, billable: filterBillable })); } catch {}
  }, [filterYear, filterDateFrom, filterDateTo, filterSourceType, filterProject, filterOffice, filterBillable]);

  // Sort
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  // Build unified list
  const allRows = useMemo(() => buildUnifiedExpenseList({
    projectExpenses: activeExpenses || [],
    officeOverheads: activeOfficeOverheads || [],
    reimbursements: reimbursements || [],
    paymentInboxExpenses,
  }), [activeExpenses, activeOfficeOverheads, reimbursements, paymentInboxExpenses]);

  // Available years: 2018 → current (plus any data years outside that range)
  const availableYears = useMemo(() => {
    const years = new Set();
    const currentYear = new Date().getFullYear();
    for (let y = 2018; y <= currentYear; y++) years.add(String(y));
    allRows.forEach(r => { if (r.date) years.add(r.date.substring(0, 4)); });
    return [...years].sort((a, b) => b.localeCompare(a));
  }, [allRows]);

  // Apply filters
  const filtered = useMemo(() => {
    let rows = filterUnifiedExpenses(allRows, {
      dateFrom: filterYear ? `${filterYear}-01-01` : (filterDateFrom || undefined),
      dateTo:   filterYear ? `${filterYear}-12-31` : (filterDateTo   || undefined),
      sourceTypes: filterSourceType ? [filterSourceType] : undefined,
      projectId: filterProject || undefined,
      officeLocation: filterOffice || undefined,
    });
    if (filterBillable === 'yes') rows = rows.filter(r => r.clientBillable);
    if (filterBillable === 'no') rows = rows.filter(r => !r.clientBillable);
    return rows;
  }, [allRows, filterYear, filterDateFrom, filterDateTo, filterSourceType, filterProject, filterOffice, filterBillable]);

  // Sort filtered rows
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let valA, valB;
      switch (sortConfig.key) {
        case 'date':
          valA = new Date(a.date || '1900-01-01'); valB = new Date(b.date || '1900-01-01');
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        case 'inputSource': {
          const labelA = getInputSourceLabel(a); const labelB = getInputSourceLabel(b);
          return sortConfig.direction === 'asc' ? labelA.localeCompare(labelB) : labelB.localeCompare(labelA);
        }
        case 'type':
          valA = (a.sourceType || '').toLowerCase(); valB = (b.sourceType || '').toLowerCase();
          return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'project':
          valA = getProjectName(a.projectId).toLowerCase(); valB = getProjectName(b.projectId).toLowerCase();
          return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'category':
          valA = (a.category || '').toLowerCase(); valB = (b.category || '').toLowerCase();
          return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'description':
          valA = (a.description || '').toLowerCase(); valB = (b.description || '').toLowerCase();
          return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'employee':
          valA = getUserName(a.employeeId).toLowerCase(); valB = getUserName(b.employeeId).toLowerCase();
          return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'amount':
          valA = a.amount ?? 0; valB = b.amount ?? 0;
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        case 'office':
          valA = (a.officeLocation || '').toLowerCase(); valB = (b.officeLocation || '').toLowerCase();
          return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'status':
          valA = (a.status || '').toLowerCase(); valB = (b.status || '').toLowerCase();
          return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'billable':
          valA = a.clientBillable ? 1 : 0; valB = b.clientBillable ? 1 : 0;
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        default: return 0;
      }
    });
  }, [filtered, sortConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Totals
  const totals = useMemo(
    () => computeExpenseTotals(filtered, convertMoney, displayCurrency),
    [filtered, convertMoney, displayCurrency]
  );

  // Helpers
  const getProjectName = (projectId) => {
    if (!projectId) return '—';
    const p = (activeProjects || []).find(x => String(x.id) === String(projectId));
    return p ? fmtProject(p) : `#${projectId}`;
  };

  const getUserName = (employeeId) => {
    if (!employeeId) return '—';
    const u = (activeUsers || []).find(x => x.id === employeeId);
    return u?.name || u?.firstName || `#${employeeId}`;
  };

  const displayAmt = (row) => {
    return convertMoney ? convertMoney(row.amount, 'INR', displayCurrency, row.date) : row.amount;
  };

  // Sort UI helpers
  const handleSortClick = (key) => {
    const newDirection = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction: newDirection });
  };
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Drill-down navigation
  const handleRowClick = (row) => {
    if (!onNavigate) return;
    if (row.sourceType === 'reimbursement') {
      onNavigate('expense-claims');
    } else {
      onNavigate('expense-admin');
    }
  };

  // Input-source label (matches Office Expenses page pattern)
  const getInputSourceLabel = (row) => {
    if (row._isLinkedReceipt) return 'Linked';
    if (row._isReimbursement) return 'Receipt';
    if (row.importSource && row.importSource.startsWith('bank-import')) return 'Bank Import';
    return 'Manual';
  };

  // Billable total
  const billableTotal = useMemo(() => {
    let sum = 0;
    for (const r of filtered) {
      if (r.clientBillable) {
        const amt = convertMoney ? convertMoney(r.amount, 'INR', displayCurrency, r.date) : r.amount;
        sum += amt;
      }
    }
    return sum;
  }, [filtered, convertMoney, displayCurrency]);

  // Client reimbursement deduction: bank payments assigned to a project
  const clientDeduction = useMemo(() => {
    let payments = paymentInboxExpenses.filter(p => p.linkedProjectId);
    if (filterProject) payments = payments.filter(p => String(p.linkedProjectId) === String(filterProject));
    const dateFrom = filterYear ? `${filterYear}-01-01` : (filterDateFrom || null);
    const dateTo   = filterYear ? `${filterYear}-12-31` : (filterDateTo   || null);
    if (dateFrom) payments = payments.filter(p => (p.txnDate || '') >= dateFrom);
    if (dateTo)   payments = payments.filter(p => (p.txnDate || '') <= dateTo);
    return payments.reduce((s, p) => {
      const amt = convertMoney ? convertMoney(p.amount, 'INR', displayCurrency, p.txnDate) : p.amount;
      return s + amt;
    }, 0);
  }, [paymentInboxExpenses, filterProject, filterYear, filterDateFrom, filterDateTo, convertMoney, displayCurrency]);

  const summaryCards = [
    { label: 'Gross Total', value: totals.total },
    ...Object.entries(totals.byType).map(([k, v]) => ({ label: SOURCE_TYPE_LABELS[k] || k, value: v })),
    { label: 'Client-Billable', value: billableTotal },
    ...(clientDeduction > 0 ? [
      { label: 'Client Reimbursements', value: -clientDeduction, isDeduction: true },
      { label: 'Net Company Cost', value: totals.total - clientDeduction, isNet: true },
    ] : []),
  ];

  const cellStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0, fontSize: '10px' };

  return (
    <div className={`brio-flex brio-flex-col brio-analytics-font ${rootClassName}`} style={{ background: '#fff', ...rootStyle }}>

      {/* HEADER BAND */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-flex brio-items-center brio-gap-xs" style={{ flexWrap: 'nowrap' }}>
              <BrioSelect value={filterYear} onChange={e => { setFilterYear(e.target.value); if (e.target.value) { setFilterDateFrom(''); setFilterDateTo(''); } }}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 70 }}>
                <option value="">All Years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </BrioSelect>
              <BrioDatePicker value={filterDateFrom} onChange={v => { setFilterDateFrom(v); if (v) setFilterYear(''); }}
                className="brio-datepicker-trigger--compact" placeholder="From"
                style={{ width: 110, fontSize: '10px' }} clearable />
              <BrioDatePicker value={filterDateTo} onChange={v => { setFilterDateTo(v); if (v) setFilterYear(''); }}
                className="brio-datepicker-trigger--compact" placeholder="To"
                style={{ width: 110, fontSize: '10px' }} clearable />
              <BrioSelect value={filterSourceType} onChange={e => setFilterSourceType(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 130 }}>
                <option value="">All Types</option>
                {SOURCE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </BrioSelect>
              <BrioSelect value={filterProject} onChange={e => setFilterProject(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px' }}>
                <option value="">All Projects</option>
                {[...(activeProjects || [])].sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || '')).map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
              </BrioSelect>
              <BrioSelect value={filterOffice} onChange={e => setFilterOffice(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 70 }}>
                <option value="">All Offices</option>
                <option value="NL">NL</option>
                <option value="IN">IN</option>
              </BrioSelect>
              <BrioSelect value={filterBillable} onChange={e => setFilterBillable(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 90 }}>
                <option value="">All Billing</option>
                <option value="yes">Client-Billable</option>
                <option value="no">Non-Billable</option>
              </BrioSelect>
            </div>
            <BrioFullScreenButton isFullScreen={isFullScreen} onToggle={toggleFullScreen} />
          </div>
        </BrioFrame>
      </div>

      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>

          {/* Summary cards */}
          <div className="brio-flex brio-gap-xs" style={{ flexWrap: 'wrap' }}>
            {summaryCards.map(c => (
              <BrioAnalyticsSurface key={c.label} padding="s" style={{ flex: '1 1 140px', minWidth: 140, ...(c.isNet ? { borderColor: 'var(--brio-gold,#9F902A)', borderWidth: 1, borderStyle: 'solid' } : {}) }}>
                <div className="brio-text-xs brio-text-grey">{c.label}</div>
                <div className="brio-font-bold" style={{ fontSize: 16, color: c.isDeduction ? 'var(--brio-teal,#2a9d8f)' : c.isNet ? 'var(--brio-gold,#9F902A)' : undefined }}>
                  {c.isDeduction ? `−${formatMoney(-c.value, displayCurrency)}` : formatMoney(c.value, displayCurrency)}
                </div>
              </BrioAnalyticsSurface>
            ))}
          </div>

          {/* Table */}
          <BrioAnalyticsSurface padding="m">
            <BrioByCode code={PRESENTATION_CODE} className="!overflow-visible">
              <BrioTable {...tableProps} style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '6%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('date')}>Date{getSortIndicator('date')}</th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('inputSource')}>Input Source{getSortIndicator('inputSource')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('type')}>Type{getSortIndicator('type')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('project')}>Project{getSortIndicator('project')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('category')}>Category{getSortIndicator('category')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('description')}>Description{getSortIndicator('description')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('employee')}>Employee{getSortIndicator('employee')}</th>
                    <th className="text-right" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('amount')}>Amount ({displaySymbol}){getSortIndicator('amount')}</th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('office')}>Office{getSortIndicator('office')}</th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('status')}>Status{getSortIndicator('status')}</th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('billable')}>Billable{getSortIndicator('billable')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 && (
                    <tr><td colSpan={11} className="brio-text-center brio-analytics-muted" style={{ padding: '24px 0' }}>
                      No expenses found.
                    </td></tr>
                  )}
                  {sorted.map(row => (
                    <tr key={row.id} className="brio-row--hoverable"
                      style={{ cursor: onNavigate ? 'pointer' : undefined, ...(row._isLinkedReceipt ? { opacity: 0.55 } : {}) }}
                      onClick={() => handleRowClick(row)}
                      title={row._isLinkedReceipt ? 'Linked receipt — not counted in totals' : onNavigate ? `Go to ${row.sourceType === 'reimbursement' ? 'Expense Claims' : 'Expense Administration'}` : undefined}>
                      <td className="brio-text-grey" style={cellStyle}>{row.date || '—'}</td>
                      <td className="text-center" style={cellStyle}>
                        {row._isLinkedReceipt ? (
                          <span style={{ fontSize: 8, color: 'var(--brio-teal)', fontStyle: 'italic' }}>Linked</span>
                        ) : row._isReimbursement ? (
                          <span className="brio-badge" style={{ fontSize: 9, padding: '1px 5px', backgroundColor: 'var(--brio-teal-light)', color: 'var(--brio-teal)' }}>Receipt</span>
                        ) : row.importSource && row.importSource.startsWith('bank-import') ? (
                          <span style={{ fontSize: 9, color: 'var(--brio-gold)' }}>Bank Import</span>
                        ) : (
                          <span style={{ fontSize: 9, color: 'var(--brio-grey)' }}>Manual</span>
                        )}
                      </td>
                      <td style={cellStyle}>
                        <span className="brio-badge brio-badge--info" style={{ fontSize: '8px', padding: '1px 4px' }}>
                          {SOURCE_TYPE_LABELS[row.sourceType] || row.sourceType}
                        </span>
                      </td>
                      <td className="brio-text-grey" style={cellStyle} title={getProjectName(row.projectId)}>
                        {getProjectName(row.projectId)}
                      </td>
                      <td className="brio-text-grey" style={cellStyle}>{row.category || '—'}</td>
                      <td className="brio-text-grey" style={cellStyle} title={row.description}>
                        {row.description || '—'}
                      </td>
                      <td className="brio-text-grey" style={cellStyle}>{getUserName(row.employeeId)}</td>
                      <td className="text-right brio-font-medium">{formatMoney(displayAmt(row), displayCurrency, row.sourceType === 'reimbursement' ? { exact: true } : undefined)}</td>
                      <td className="text-center brio-text-grey" style={{ fontSize: '10px' }}>{row.officeLocation}</td>
                      <td className="text-center">
                        {row.status && (
                          <span className="brio-badge brio-badge--pending" style={{ fontSize: '8px', padding: '1px 4px' }}>
                            {row.status}
                          </span>
                        )}
                      </td>
                      <td className="text-center" style={{ fontSize: '10px' }}>
                        {row.clientBillable ? (
                          <span style={{ color: 'var(--brio-gold, #9F902A)', fontWeight: 600 }}>$</span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </BrioTable>
            </BrioByCode>

            {filtered.length > 0 && (
              <div style={{ marginTop: 8, padding: '4px 8px', fontSize: 11, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <span className="brio-text-grey">{filtered.length} expenses</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <span className="brio-font-bold">Gross: {formatMoney(totals.total, displayCurrency)}</span>
                  {clientDeduction > 0 && (
                    <>
                      <span style={{ color: 'var(--brio-teal,#2a9d8f)' }}>Less reimbursements: −{formatMoney(clientDeduction, displayCurrency)}</span>
                      <span className="brio-font-bold" style={{ color: 'var(--brio-gold,#9F902A)', borderTop: '1px solid var(--brio-border,#d4d0c8)', paddingTop: 2 }}>Net: {formatMoney(totals.total - clientDeduction, displayCurrency)}</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </BrioAnalyticsSurface>
        </div>
      </BrioFrame>
    </div>
  );
}
